/**
 * Rotator (SCN-612) — cycles the playlist's enabled slides with a 0.5s crossfade.
 *
 *  - each slide shows for durationSeconds, falling back to the playlist default
 *  - Video slides (uploaded or YouTube) drive their own advance timing — see Slide.tsx's timing
 *    rule — so no generic timer is set for them here
 *  - when a new playlist arrives mid-rotation it is adopted at the next slide boundary, resuming
 *    from the next position rather than snapping back to slide 1
 *  - an empty (all-disabled) playlist shows the holding message
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { CROSSFADE_MS, FALLBACK_SLIDE_MS } from '@/config';
import type { DevicePlaylist, DeviceSlide } from '@/types';
import { Slide } from './Slide';
import { HoldingScreen } from './HoldingScreen';

const enabledOf = (p: DevicePlaylist): DeviceSlide[] =>
  [...p.slides].filter((s) => s.isEnabled).sort((a, b) => a.order - b.order);

/** Cheap structural signature so we only adopt a playlist that actually changed. */
const signature = (p: DevicePlaylist): string =>
  `${p.playlistId}|${p.defaultSlideDurationSeconds}|` +
  enabledOf(p)
    .map((s) => `${s.id}:${s.order}:${s.durationSeconds ?? 'd'}:${JSON.stringify(s.payload)}`)
    .join('~');

const slideMs = (slide: DeviceSlide, playlist: DevicePlaylist): number =>
  (slide.durationSeconds ?? playlist.defaultSlideDurationSeconds ?? FALLBACK_SLIDE_MS / 1000) * 1000;

interface Layer {
  key: number;
  slide: DeviceSlide;
  playlist: DevicePlaylist;
  state: 'in' | 'out';
}

export function Rotator({ playlist }: { playlist: DevicePlaylist }) {
  const [active, setActive] = useState<DevicePlaylist>(playlist);
  const [index, setIndex] = useState(0);
  const [layers, setLayers] = useState<Layer[]>([]);

  const pendingRef = useRef<DevicePlaylist | null>(null);
  const keyRef = useRef(0);
  const timerRef = useRef<ReturnType<typeof setTimeout>>();

  const slides = useMemo(() => enabledOf(active), [active]);
  const current = slides[index];

  // Queue a genuinely different playlist for adoption at the next boundary.
  useEffect(() => {
    if (signature(playlist) !== signature(active)) pendingRef.current = playlist;
  }, [playlist, active]);

  // Seed the first layer.
  // Seed the first layer once we have a slide (deps intentionally just `current`).
  useEffect(() => {
    if (layers.length === 0 && current) {
      setLayers([{ key: keyRef.current++, slide: current, playlist: active, state: 'in' }]);
    }
  }, [current, active, layers.length]);

  const advance = () => {
    const pending = pendingRef.current;
    let nextPlaylist = active;
    let nextIndex = index + 1;

    if (pending) {
      pendingRef.current = null;
      nextPlaylist = pending;
      const nextEnabled = enabledOf(pending);
      nextIndex = nextEnabled.length ? nextIndex % nextEnabled.length : 0;
      setActive(pending);
    } else {
      nextIndex = slides.length ? nextIndex % slides.length : 0;
    }

    const nextSlide = enabledOf(nextPlaylist)[nextIndex];
    if (!nextSlide) return;

    keyRef.current += 1;
    setIndex(nextIndex);
    setLayers((prev) => [
      ...prev.map((l) => ({ ...l, state: 'out' as const })),
      { key: keyRef.current, slide: nextSlide, playlist: nextPlaylist, state: 'in' as const },
    ]);
  };

  // Drop faded-out layers once the transition has finished.
  useEffect(() => {
    if (!layers.some((l) => l.state === 'out')) return;
    const t = setTimeout(() => {
      setLayers((prev) => prev.filter((l) => l.state === 'in'));
    }, CROSSFADE_MS + 50);
    return () => clearTimeout(t);
  }, [layers]);

  // Per-slide timer for every type except Video, which advances itself (see Slide.tsx).
  useEffect(() => {
    if (!current || current.type === 'Video') return;
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(advance, slideMs(current, active));
    return () => clearTimeout(timerRef.current);
    // `advance` is recreated every render but always reads fresh state; keying the timer on the
    // current slide + playlist is what we want.
  }, [current, active]);

  if (slides.length === 0) return <HoldingScreen />;

  return (
    <div className="rotator">
      {layers.map((l) => (
        <div key={l.key} className={`rotator__layer rotator__layer--${l.state}`}>
          <Slide
            slide={l.slide}
            targetDurationSeconds={slideMs(l.slide, l.playlist) / 1000}
            onAdvance={advance}
          />
        </div>
      ))}

      {current && current.type !== 'Video' && (
        <div className="rotator__progress">
          <div
            className="rotator__progress-fill"
            key={index}
            style={{ animationDuration: `${slideMs(current, active)}ms` }}
          />
        </div>
      )}

      {slides.length > 1 && slides.length <= 12 && (
        <div className="rotator__dots">
          {slides.map((s, i) => (
            <span key={s.id} className={`rotator__dot${i === index ? ' rotator__dot--active' : ''}`} />
          ))}
        </div>
      )}
    </div>
  );
}
